import { env } from "@oefr/env/shared"
import { ClientError } from "graphql-request"
import { type LogContext, reportEvent } from "@krakentech/blueprint-utils/logging"

export enum LoggerInfoEventType {
    GenericInfo = "generic_info",
}

export enum LoggerWarningEventType {
    BlockedRequest = "blocked_request",
    MissingData = "missing_data",
    KnownEligibilityError = "known_eligibility_error",
    KnownAuthenticationError = "known_authentication_error",
    EffyLeadSubscriptionFailed = "effy_lead_subscription_failed",
}

export enum LoggerErrorEventType {
    GraphQLError = "graphql_error",
    InvalidDataError = "invalid_data_error",
    UnexpectedError = "unexpected_error",
}

export type LoggerBaseContext = Omit<
    LogContext,
    "reportTo" | "severity" | "eventType" | "description"
>

export type LoggerInfoContext = LoggerBaseContext & {
    description?: string
}

export type LoggerWarningContext = LoggerBaseContext & {
    eventType: LoggerWarningEventType
    description: string
}

export type LoggerErrorContext = LoggerBaseContext & {
    eventType?: LoggerErrorEventType
    description?: string
}

class Logger {
    private isDisabled = !!process.env.NEXT_PUBLIC_PLAYWRIGHT_MODE

    private report(context: Omit<LogContext, "reportTo">) {
        if (this.isDisabled) return

        reportEvent({
            ...context,
            reportTo: "datadog",
        })
    }

    // ClientError from graphql-request serialises full request variables
    // (including PII like IBAN, names) into its message. Retain only the
    // GraphQL error messages as the Error, and move the Kraken response
    // (errors, extensions) to `extra` so it remains visible in Datadog.
    private normaliseError(context: { error?: unknown; extra?: Record<string, unknown> }) {
        return context.error instanceof ClientError
            ? {
                  error: new Error(
                      context.error.response.errors?.map((e) => e.message).join(", ") ??
                          "GraphQL error"
                  ),
                  extra: {
                      ...context.extra,
                      graphqlErrors: context.error.response.errors,
                  },
              }
            : { error: context.error, extra: context.extra }
    }

    info(context: LoggerInfoContext) {
        const { error, extra } = this.normaliseError(context)

        let defaultDescription = "Info log"

        if (context.meta?.source) {
            defaultDescription += ` in ${context.meta.source}`
        }

        this.report({
            ...context,
            error,
            extra,
            severity: "info",
            eventType: LoggerInfoEventType.GenericInfo,
            description:
                context.description ??
                (error instanceof Error ? error.message : defaultDescription),
        })
    }

    warning(context: LoggerWarningContext) {
        this.report({
            ...context,
            severity: "warning",
        })
    }

    error(context: LoggerErrorContext) {
        let defaultDescription = "An unexpected error occurred"

        if (context.meta?.source) {
            defaultDescription += ` in ${context.meta.source}`
        }

        const { error, extra } = this.normaliseError(context)

        this.report({
            ...context,
            env: env.NODE_ENV,
            error,
            extra,
            severity: "error",
            eventType: context.eventType ?? LoggerErrorEventType.UnexpectedError,
            description: context.description ?? defaultDescription,
        })
    }
}

export const logger = new Logger()
